import React, { useState, useCallback, useEffect, useContext } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useMediaQuery, useTheme } from '@mui/material';
import { useNavigate, useLocation } from 'react-router';
import { useDrawer } from '../contexts/drawerContextProvider';
import Menu from '@mui/icons-material/Menu';
import { Drawer, DrawerBody, DrawerHeader, DrawerNavGroup, DrawerNavItem } from '@brightlayer-ui/react-components';
import * as menuItems from "./menuRoutes";
import { useTranslation } from 'react-i18next';
import useClasses from '../hooks/useClassesHook';
import { StateContext } from '../contexts/StateContext';
import {Roles} from '../constants/roles';
import { PortalApiToken } from '../services/api/api-token';
import { Eaton } from '@brightlayer-ui/icons-mui';
import { DTPG_NAVIGATION_MENU } from '../menuMetaData';
import { LINEPRO_NAVIGATION_MENU } from '../falconMenuMetaData';
import { logoutUser } from '../actions/LogoutAction';
import { debounce } from 'debounce';
import { filterRequestedProducts } from '../pages/Products/ProductHomeComponent';
import IconButton from '@mui/material/IconButton';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import Badge from '@mui/material/Badge';
import { PortalClientStorage } from '../services/client-storage/storage';
import { isDevPortalEnvironment } from '../utils/deploymentEnvironment';

const styles = (theme) => ({
    root: {
        height: "auto !important",
        fontSize: "14px !important"
    },
divider: {
        borderTop: "1px solid #e0e0e0",
    },
    title: {
        fontWeight: '550 !important'
    }
});

export const sortArrayAlphabetically = (inputArray) => {
    inputArray.sort(function (a, b) {
        if (a.name < b.name) {
            return -1;
        }
        if (a.name > b.name) {
            return 1;
        }
        return 0;
    });
    return inputArray;
};

export const setApprovedProducts = (reqProducts, menuItems, setProductsMenu) => {
    let products = reqProducts && reqProducts?.filter((prod) => prod?.approved);
    if (products && products.length > 0) {
        // const approvedProds = products?.filter((product) => product.approved );
        // apply filter on approvedProds on the basis of requested prodids from current API
        const AllProductsMenu = menuItems.PRODUCTS_MENU[0];
        let productMenu = menuItems.PRODUCTS_MENU;
        productMenu = sortArrayAlphabetically(productMenu);
        products = sortArrayAlphabetically(products);
        const filterProductMenu =
            products.length !== 0 &&
            products.map((approvedProductItem) => productMenu.filter((item) => item.name === approvedProductItem.name));
        const finalProductMenu = [AllProductsMenu, ...filterProductMenu].flat();
        setProductsMenu(finalProductMenu);
    }
};

export const getAssetManagementMenu = (massOtaEnabled, hasDrEventOverviewRole) => [
    ...(massOtaEnabled ? menuItems.dlm_menu : []),
    ...(hasDrEventOverviewRole ? menuItems.DR_EVENTS_MENU : []),
];

export const handleNavigationWithMobileView = (
    link,
    toggleVideoShow,
    setIsNavMenuClicked,
    handleNavigate,
    isMobile,
    setDrawerOpen
) => {
    if (link.includes('products')) {
        toggleVideoShow(false);
    }
    if (
        link === '/factoryreset/find-device-by-type-and-number' ||
        link === '/factoryreset/find-device-key-by-registration' ||
        link === '/factoryreset/list-devices'
    ) {
        setIsNavMenuClicked(true);
    }
    handleNavigate(link);
    isMobile ? setDrawerOpen(false) : setDrawerOpen(true);
};

export const NavigationDrawer = () => {
    const { drawerOpen, setDrawerOpen } = useDrawer();
    const {forcedClosed, setForcedClosed} =  useDrawer();
    const {variant,setVariant} =  useDrawer();
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [selected, setSelected] = useState(location?.pathname);
    const {reqProducts, setReqProducts} = useContext(StateContext);
    const {allProducts} = useContext(StateContext);
    const {prodProductsLink} = useContext(StateContext);
    const {setIsNavMenuClicked} = useContext(StateContext);
    const { toggleVideoShow } = useContext(StateContext);
    const {sessionLogout} = useContext(StateContext);
    const { notifBadgeCount } = useContext(StateContext);
    const { deviceUniqueIdentificationNumber, setDeviceUniqueIdentificationNumber } = useContext(StateContext);


    const { t } = useTranslation();
    const classes = useClasses(styles);
    const [link] = useState(null);
    const [dtpgProceedClicked, setDtpgProceedClicked] = useState(false);
    const [falconProceedClicked, setFalconProceedClicked] = useState(false);
    const currentUserData = PortalClientStorage.getData('currentUser', true);
    const massOtaEnabled = currentUserData ? JSON.parse(currentUserData)?.includes("OTADLMAdmin") : false;
    const hasYukonSystemsRole = currentUserData
        ? JSON.parse(currentUserData)?.includes('YukonSupport')
        : false;
    const isDevEnvironment = isDevPortalEnvironment();
    const hasYukonSystemsAccess = hasYukonSystemsRole && isDevEnvironment;
    const hasDrEventOverviewRole = massOtaEnabled && isDevEnvironment;
    const hasNotificationsRole = (massOtaEnabled || hasYukonSystemsRole) && isDevEnvironment;
   
    const isFalconUser =  currentUserData ? JSON.parse(currentUserData)?.includes('NovaLineProUser') : false;
   
    window.onmessage = function(e) { // inside the parent app
        if (e?.data === "DTPG-Version101-Navigation-Menu-Proceed-Clicked") {
            setDtpgProceedClicked(true);
        }
        if (e?.data === "DTPG-Version101-Navigation-Menu-Logout-Clicked") {
            setDtpgProceedClicked(false);
        }
        if (e?.data === "Falcon-Version101-Navigation-Menu-Proceed-Clicked") {
            setFalconProceedClicked(true);
        }
        //Falcon-Version101-Navigation-Menu-Device-Id-${deviceIdValue}
        if (typeof e?.data === "string" && e.data.startsWith("Falcon-Version101-Navigation-Menu-Device-Id-")) {
            const deviceIdValue = e?.data?.split("Falcon-Version101-Navigation-Menu-Device-Id-")[1];
            console.log("Received device ID from Falcon iframe:", deviceIdValue);
            setDeviceUniqueIdentificationNumber(deviceIdValue);
        }
        if (e?.data === "Falcon-Version101-Navigation-Menu-Device-Id-Removed") {
            console.log("Device ID removed from Falcon iframe - test");
            setDeviceUniqueIdentificationNumber('');
        }

        
        if (e?.data === "Falcon-Version101-Navigation-Menu-Logout-Clicked") {
            setFalconProceedClicked(false);
            const iframe = document.getElementById('inlineAPPiframe');
            if (iframe) {
                // Reload the Falcon iframe to its entry page without changing the
                // parent route, so RouterWrapper's auth useEffect doesn't re-fire.
                iframe.src = iframe.src;
            }
        }
    };    

    const handleNavigate = useCallback(
        (id) => {
            var iframe = document.getElementById('inlineAPPiframe');
            if (id === '/linepro/settings' && iframe) {
                // Always reload the Falcon iframe to its entry page when the
                // NovaLinePro menu is clicked, regardless of the iframe's
                // current state (e.g. user already proceeded into Falcon).
                iframe.src = iframe.src;
                setFalconProceedClicked(false);
            }
            iframe && iframe.contentWindow.postMessage(id, '*');
            document.dispatchEvent(new CustomEvent('DTPG-Navigation-Menu-Clicked', { detail: id }));
            navigate(id);
            setSelected(id);
        },
        [navigate, setSelected]
    );

     const handleUserActivity = () => {
        if(sessionLogout){
            logoutUser();
            window?.location?.reload();
        }
    }
    
    useEffect(() => {
        document.addEventListener('mousemove', debounce(handleUserActivity,10000));
        document.addEventListener('keydown', debounce(handleUserActivity,5000));
        document.addEventListener('scroll', debounce(handleUserActivity,10000));

        return () => {
            document.removeEventListener('mousemove', handleUserActivity);
            document.removeEventListener('keydown', handleUserActivity);
            document.removeEventListener('scroll', handleUserActivity);
        }
    }, []);

    useEffect(() => {
        handleNavigate(link)
    }, [link])

    useEffect(() => {
        isMobile ? setDrawerOpen(false) : setDrawerOpen(true);
    }, [])

    useEffect(() => {
        let earlierReqProduct = filterRequestedProducts(allProducts, () => {}, true);
        setReqProducts(earlierReqProduct);
    },[allProducts])

    useEffect(() => {
        
        if(isMobile && window?.location?.pathname == "/productowner/customers"){
            setDrawerOpen(false);
        }
    })

   

    const [productsMenu, setProductsMenu] = useState([menuItems.PRODUCTS_MENU[0]]);


    const identifyMenusForCurrentUser = (title) => {
        if(Roles[title] === undefined) return;
        const tempRoleArray = [];
        const currentMenuRoles = Roles && Roles[title];
        const currentUserRoles = JSON.parse(window.localStorage.getItem('currentUser'));
        currentUserRoles && currentUserRoles.map((userRole) => {
            currentMenuRoles?.map((menuRole) => {
                const shouldPush = (userRole === menuRole);
                if (shouldPush) tempRoleArray.push(userRole);
            })
        });
        return !(tempRoleArray?.length > 0);
    }

    const shouldNotDisplayMenuORSubmenuItem = (titleKey) => {
        let transTitle = t(`translation:${titleKey}`);
        transTitle = transTitle?.replace(/translation:/g, '');
        if (!(transTitle == 'Products') && identifyMenusForCurrentUser(transTitle)) {
            return true;
        }
    }


    useEffect(() => {
        const rootDrawerLink = document?.querySelector('.BluiDrawerLayout-root');
        rootDrawerLink?.classList?.remove('selected-nav-item');
        const isToken = PortalApiToken.doesTokenExist();
        if(isToken){
            setApprovedProducts(reqProducts,menuItems,setProductsMenu)
        }
    },[reqProducts, prodProductsLink]); // FIX: Added prodProductsLink as dependency to re-render menu when products load

    const renderMenuItems = (menuItems) => {
        let renderedMenu = [];
        if (menuItems) {
            menuItems.forEach((menuItem) => {
                if(shouldNotDisplayMenuORSubmenuItem(menuItem?.titleKey)) return;
                let tempLink = menuItem?.link;
                if (menuItem?.link?.includes('products')) {
                    // FIX: Check if prodProductsLink has data before accessing
                    // This prevents race condition where menu renders before API call completes
                    if (!prodProductsLink || prodProductsLink.length === 0) {
                        return; // Skip this item until data is loaded
                    }
                    
                    //Add a newly created product link in below format, to fine tune id with api response
                    //If not it will lead to all products getting highlighted on click of new product from the drawer 
                    let customLink;
                    if (menuItem?.titleKey?.includes('allproducts')) {
                        tempLink = '/products/all';
                    } else if (menuItem?.titleKey?.includes('codesys')) {
                        customLink = prodProductsLink?.filter((el) => el?.name == 'CODESYS Workbench');
                        if (!customLink || customLink.length === 0) {
                            return;
                        }
                        tempLink = `/products/${customLink[0]?.id}`;
                    } else if (menuItem?.titleKey?.includes('ied')) {
                        customLink = prodProductsLink?.filter((el) => el?.name == 'IED Manager Suite');
                        if (!customLink || customLink.length === 0) {
                            return;
                        }
                        tempLink = `/products/${customLink[0]?.id}`;
                    } else if (menuItem?.titleKey?.includes('ieddemo')) {
                        customLink = prodProductsLink?.filter((el) => el?.name == 'Demo VM - IED Manager Suite');
                        if (!customLink || customLink.length === 0) {
                            return;
                        }
                        tempLink = `/products/${customLink[0]?.id}`;                        
                    } else if (menuItem.titleKey.includes('distributedio')) {
                        customLink = prodProductsLink?.filter(
                            (el) => el?.name == 'Distributed I/O and RTU Upgrade solution'
                        );
                        if (!customLink || customLink.length === 0) {
                            return;
                        }
                        tempLink = `/products/${customLink[0]?.id}`;
                    } else if (menuItem?.titleKey?.includes('smpgateway')) {
                        customLink = prodProductsLink?.filter((el) => el?.name == 'SMP Gateway Automation Platform');
                        if (!customLink || customLink.length === 0) {
                            return;
                        }
                        tempLink = `/products/${customLink[0]?.id}`;
                    } else if (menuItem?.titleKey?.includes('smpconfigtool')) {
                        customLink = prodProductsLink?.filter((el) => el?.name == 'SMP Configuration Comparison Tool');
                        if (!customLink || customLink.length === 0) {
                            return;
                        }
                        tempLink = `/products/${customLink[0]?.id}`;
                    } else if (menuItem?.titleKey?.includes('smptools')) {
                        customLink = prodProductsLink?.filter((el) => el?.name == 'SMP Tools');
                        if (!customLink || customLink.length === 0) {
                            return;
                        }
                        tempLink = `/products/${customLink[0]?.id}`;
                    }else if (menuItem?.titleKey?.includes('smpdaplatform')) {
                        customLink = prodProductsLink?.filter((el) => el?.name == 'SMP Distribution Automation Platform');
                        if (!customLink || customLink.length === 0) {
                            return;
                        }
                        tempLink = `/products/${customLink[0]?.id}`;
                    }else if (menuItem?.titleKey?.includes('visualtnd')) {
                        customLink = prodProductsLink?.filter((el) => el?.name == 'Visual T&D');
                        if (!customLink || customLink.length === 0) {
                            return;
                        }
                        tempLink = `/products/${customLink[0]?.id}`;
                    } else if (menuItem?.titleKey?.includes('yukonfeeder')) {
                        customLink = prodProductsLink?.filter((el) => el?.name == 'Yukon Feeder Automation');
                        if (!customLink || customLink.length === 0) {
                            return;
                        }
                        tempLink = `/products/${customLink[0]?.id}`;
                    } else if (menuItem?.titleKey?.includes('yukongridserver')) {
                        customLink = prodProductsLink?.filter((el) => el?.name == 'Yukon GridServer');
                        if (!customLink || customLink.length === 0) {
                            return;
                        }
                        tempLink = `/products/${customLink[0]?.id}`;
                    } else if (menuItem?.titleKey?.includes('feederautomationmanager')) {
                        customLink = prodProductsLink?.filter((el) => el?.name == 'Feeder Automation Manager');
                        if (!customLink || customLink.length === 0) {
                            return;
                        }
                        tempLink = `/products/${customLink[0]?.id}`;
                    } else if (menuItem?.titleKey?.includes('yukonenterprisesoftwareplatform')) {
                        customLink = prodProductsLink?.filter((el) => el?.name == 'Yukon Enterprise Software Platform');
                        if (!customLink || customLink.length === 0) {
                            return;
                        }
                        tempLink = `/products/${customLink[0]?.id}`;
                    } else if (menuItem?.titleKey?.includes('yukonnetworkmanager')) {
                        customLink = prodProductsLink?.filter((el) => el?.name == 'Yukon Network Manager');
                        if (!customLink || customLink.length === 0) {
                            return;
                        }
                        tempLink = `/products/${customLink[0]?.id}`;                                                
                    } else if (menuItem?.titleKey?.includes('dtpg')){
                        customLink = prodProductsLink?.filter((el) => el?.name == 'DTPG Configuration Tool');
                        if (!customLink || customLink.length === 0) {
                            return;
                        }
                        tempLink = `/products/${customLink[0]?.id}`;
                    } else if (menuItem?.titleKey?.includes('magnefixicap')){
                        customLink = prodProductsLink?.filter((el) => el?.name == 'Magnefix MD4 iCap');
                        if (!customLink || customLink.length === 0) {
                            return;
                        }
                        tempLink = `/products/${customLink[0]?.id}`;
                    } else if (menuItem?.titleKey?.includes('edgeplatform')){
                        customLink = prodProductsLink?.filter((el) => el?.name == 'Edge Platform');
                        if (!customLink || customLink.length === 0) {
                            return;
                        }
                        tempLink = `/products/${customLink[0]?.id}`;
                    }                            
                }
              
                renderedMenu.push(renderMenuItem(menuItem.titleKey, t(menuItem.titleKey), tempLink, menuItem.dividerBefore, menuItem.icon, menuItem.children || null));
            });
        }
        return renderedMenu;
    };


    const renderMenuItem = (id, title, link, dividerBefore, icon, menuItems, skipRoleCheck = false, iconColor = '#007bc1') => {
      
        let translatedTitle = t(`translation:${title}`);
        const classNameToApply =  translatedTitle?.toLowerCase().replace(/[&\/\\# +()$~%.'":*?<>{}]/g, '-tql-');
        let hasNumber = /\d/;
        let isNavItemHeading = hasNumber.test(id);
        if(!skipRoleCheck && shouldNotDisplayMenuORSubmenuItem(title)) return;
        const selectedNavItemClass = (!isNavItemHeading && selected === link ? 'selected-nav-item' : '');
        translatedTitle = translatedTitle?.replace(/translation:/g, '');
        return (
            <DrawerNavItem
                key={id}
                itemID={id}
                hidePadding={true}
                InfoListItemProps={{ "data-url": link, icon: icon, iconColor: iconColor, iconAlign: 'right', ripple: true}}
                classes={dividerBefore ? {} : { root: classes.root, title: isNavItemHeading ? classes.title: null }}
                onClick={(link != null ? () => handleNavigationWithMobileView(link,toggleVideoShow,setIsNavMenuClicked,handleNavigate,isMobile,setDrawerOpen) : undefined)}
                title={translatedTitle}
                className={`side-nav-section-${translatedTitle} ${selectedNavItemClass} ${classNameToApply}`}
            >
                {menuItems != null ? renderMenuItems(menuItems) : null}
            </DrawerNavItem>
        );
    };

    useEffect(() => {
        setVariant(isMobile? 'temporary' : 'persistent');
        if(!isMobile){
            setForcedClosed(false)
            setDrawerOpen(true);
        }
        
    },[isMobile])

    useEffect(() => {        
        const pageHeader = document.querySelector('.page-heading');
        forcedClosed
            ? pageHeader?.classList.add('drawer-closed')
            : pageHeader?.classList.remove('drawer-closed');
    },[forcedClosed])

    const handleIconClick = () => {        
        setDrawerOpen(false);
        setForcedClosed(true);
        setVariant('temporary');
    };

    return (
        <>
                <Drawer
                    data-testid="navigation-drawer"
                    open={drawerOpen}
                    ModalProps={{
                        onBackdropClick: () => {
                            setDrawerOpen(false);
                        },
                    }}
                    variant={variant}
                    activeItem={selected}
                    width={'17.3rem'}
                >
                    <DrawerHeader
                    data-testid="drawer-header"
                    titleContent={
                        <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'row', alignItems: 'center', width:'100%' }}>
                            
                            <Eaton style={{ width: isMobile ? '60px' : '80px' , marginTop: 'auto',marginBottom: 'auto',paddingLeft: isMobile ? '0px' : '20px' }}/>
                            <Typography variant="subtitle" style={{ margin: 'auto', paddingRight: '20px'}}>
                                Grid Solutions
                            </Typography>
                            <IconButton hidden={isMobile} color="inherit" onClick={handleIconClick} size="large" data-testid='navigate-before-icon'>
                                <NavigateBeforeIcon />
                            </IconButton>
                        </Box>
                    }
                        icon={isMobile || forcedClosed ?  <Menu /> : undefined}
                        onIconClick={() => {
                            const value = isMobile ? !drawerOpen : true;
                            setDrawerOpen(value);
                        }}
                        onClick={() => {
                            if (!isMobile) return;
                            const value = isMobile ? !drawerOpen : true;
                            setDrawerOpen(value);
                            const pageHeader = document.querySelector('.page-heading');
                            drawerOpen
                                ? pageHeader.classList.add('drawer-closed')
                                : pageHeader.classList.remove('drawer-closed');
                        }}
                    />
                    <DrawerBody>
                        <DrawerNavGroup
                        data-testid="drawer-nav-group"
                            titleDivider={true}
                            titleColor={'rgba(0,0,0,.87)'}
                            className="drawer-enablity drawer-navgroup-grid"
                        >
                            {renderMenuItem('1', t('translation:menu.PRODUCTS'), null, false, menuItems.ROOT_MENU_META['1'].icon, productsMenu, false, 'inherit')}
                            {renderMenuItem(
                                '2',
                                t('translation:menu.OP_ANALYTICS'),
                                null,
                                false,
                                menuItems.ROOT_MENU_META['2'].icon,
                                menuItems.OP_ANALYTICS_MENU,
                                false,
                                'inherit'
                            )}
                            {renderMenuItem(
                                '3',
                                t('translation:menu.SMP_FILES_ANALYTICS'),
                                null,
                                false,
                                menuItems.ROOT_MENU_META['3'].icon,
                                menuItems.SMP_FILES_ANALYTICS_MENU,
                                false,
                                'inherit'
                            )}
                            {renderMenuItem('4', t('translation:menu.RULES'), null, false, menuItems.ROOT_MENU_META['4'].icon, menuItems.RULES_MENU, false, 'inherit')}
                            {renderMenuItem(
                                '5',
                                t('translation:menu.PRODUCT_OWNER'),
                                null,
                                false,
                                menuItems.ROOT_MENU_META['5'].icon,
                                menuItems.PRODUCT_OWNER_MENU,
                                false,
                                'inherit'
                            )}
                            {renderMenuItem(
                                '6',
                                t('translation:menu.FACTORY_RESET'),
                                null,
                                false,
                                menuItems.ROOT_MENU_META['6'].icon,
                                menuItems.FACTORY_RESET_MENU,
                                false,
                                'inherit'
                            )}
                            {!dtpgProceedClicked &&
                                renderMenuItem('7', t('translation:menu.DTPG'), '/dtpg/manage', false, menuItems.ROOT_MENU_META['7'].icon, null, false, 'inherit')}
                            {dtpgProceedClicked &&
                                renderMenuItem('7', t('translation:menu.DTPG'), null, false, menuItems.ROOT_MENU_META['7'].icon, DTPG_NAVIGATION_MENU, false, 'inherit')}
                            {renderMenuItem('8', t('translation:menu.EDGE_PLATFORM_MENU'), null, false, menuItems.ROOT_MENU_META['8'].icon, menuItems.EDGE_PLATFORM_NAVIGATION_MENU, false, 'inherit')}
                            
                            {renderMenuItem(
                                '9',
                                t('translation:menu.LCRS_MANAGEMENT'),
                                null,
                                false,
                                menuItems.ROOT_MENU_META['9'].icon,
                                menuItems.LCRS_MANAGEMENT_MENU,
                                false,
                                'inherit'
                            )}

                            {(massOtaEnabled || hasDrEventOverviewRole) && renderMenuItem(
                                '10',
                                t('translation:menu.ASSET_MANAGEMENT_MENU'),
                                null,
                                false,
                                menuItems.ROOT_MENU_META['10'].icon,
                                getAssetManagementMenu(massOtaEnabled, hasDrEventOverviewRole),
                                false,
                                'inherit'
                            )}

                            {hasYukonSystemsAccess && renderMenuItem(
                                '11',
                                t('translation:menu.YUKON_SYSTEMS_MENU'),
                                null,
                                false,
                                menuItems.ROOT_MENU_META['11'].icon,
                                menuItems.YUKON_SYSTEMS_MENU,
                                false,
                                'inherit'
                            )}
                            {hasNotificationsRole && renderMenuItem(
                                '12',
                                t('translation:menu.NOTIFICATIONS'),
                                null,
                                false,
                                menuItems.ROOT_MENU_META['12'].icon,
                                [
                                    {
                                        ...menuItems.NOTIFICATIONS_MENU[0],
                                        icon: (
                                            <Badge badgeContent={notifBadgeCount} color="error" invisible={notifBadgeCount === 0}>
                                                {menuItems.NOTIFICATIONS_MENU[0].icon}
                                            </Badge>
                                        ),
                                    },
                                    ...menuItems.NOTIFICATIONS_MENU.slice(1),
                                ],
                                false,
                                'inherit'
                            )}

                            {isFalconUser && !falconProceedClicked &&
                                renderMenuItem('13', t('translation:menu.LINEPRO'), '/linepro/settings', false, menuItems.ROOT_MENU_META['13'].icon, null, false, 'inherit')}
                            {isFalconUser && falconProceedClicked &&
                                renderMenuItem('13', t('translation:menu.LINEPRO'), null, false, menuItems.ROOT_MENU_META['13'].icon, LINEPRO_NAVIGATION_MENU, false, 'inherit')}
                          
                        </DrawerNavGroup>
                    </DrawerBody>
                </Drawer>

        </>
    );
};